import React from "react";
import { useParams } from "react-router-dom";
import { orderBy } from "lodash";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/solid";
import { isWithinInterval } from "date-fns";
import { useTranslation } from "react-i18next";

import TransactionHistoryTable from "@components/form/transactionHistory/TransactionHistoryTable";
import Heading from "@components/layout/Heading";
import Loading from "@components/shared/Loading";
import Label from "@components/form/Label";
import SelectAccountingPeriod from "@components/form/SelectAccountingPeriod";
import { useAllEventTransactions } from "@hooks/query/useAllEventTransactions";
import { useSubscriber } from "@hooks/query/useSubscriber";
import { useAppContext } from "@context/AppContext";
import { useAllAccountingPeriods } from "@hooks/query/zenith/useAllAccountingPeriods";

const TransactionHistoryTab: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { setInfoPanel } = useAppContext();
  const { id: subscriberId = "", level0ResourceId = "" } = useParams();
  const [selectedAccountingPeriod, setSelectedAccountingPeriod] =
    React.useState<{ id: string; periodStart: Date; periodEnd: Date }>();
  const { data: subscriber = {}, isLoading: isSubscriberLoading } =
    useSubscriber(subscriberId);
  const { data = {}, isLoading: isEventTransactionsLoading } =
    useAllEventTransactions({
      params: {
        walletId: subscriber.walletId,
        includeParentId: true,
        limit: -1,
      },
      enabled: Boolean(subscriber.walletId),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    });
  useAllAccountingPeriods({
    params: {
      level1ResourceId: subscriber?.level1ResourceId,
      isActive: true,
    },
    enabled: Boolean(subscriber?.level1ResourceId),
    onSuccess: (data: any) => {
      const currentAP = data.find((i: any) => i.isActive);

      if (currentAP && !selectedAccountingPeriod?.id) {
        setSelectedAccountingPeriod({
          id: currentAP.id,
          periodStart: new Date(currentAP.periodStart),
          periodEnd: new Date(currentAP.periodEnd),
        });
      }
    },
  });

  React.useEffect(() => {
    setInfoPanel();
  }, [setInfoPanel]);

  if (isSubscriberLoading || isEventTransactionsLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const eventTransactions = data.eventTransactions ?? [];

  const filtered = eventTransactions
    ?.filter(
      (log: any) =>
        Boolean(log.transactionAmount) &&
        Number(log.transactionAmount) !== 0 &&
        log.impactLevel0ResourceId === level0ResourceId &&
        log.eventType?.name !== "OveruseAdjustmentTokenTransfer",
    )
    .filter((log: any) => {
      return selectedAccountingPeriod
        ? isWithinInterval(new Date(log.effectiveAt), {
            start: selectedAccountingPeriod.periodStart,
            end: selectedAccountingPeriod.periodEnd,
          })
        : true;
    });

  return (
    <div className="flex flex-col grow space-y-4">
      <Heading className="flex items-center gap-2">
        <ArrowsRightLeftIcon className="w-6 h-6 shrink-0" />{" "}
        {t("audit_trail.widget_title")}
      </Heading>

      <form>
        <div className="max-w-sm">
          <Label htmlFor="accountingPeriod">
            {t("common.accounting_period")}
          </Label>
          <SelectAccountingPeriod
            inputId="accountingPeriod"
            level1ResourceId={subscriber.level1ResourceId}
            showTag
            value={selectedAccountingPeriod}
            onChange={(e: any) => {
              setSelectedAccountingPeriod(
                e?.value && {
                  id: e?.value?.id,
                  periodStart: new Date(e?.value?.periodStart),
                  periodEnd: new Date(e?.value?.periodEnd),
                },
              );
            }}
          />
        </div>
      </form>

      <TransactionHistoryTable
        eventTransactions={orderBy(filtered, ["effectiveAt"], ["desc"])}
        stickyHeader
      />
    </div>
  );
};

export default TransactionHistoryTab;
